import { removeChildren } from './uiUtils';
import { resizeLongSuggestionsToFit } from './toolsUI';
import {
	clickedSparkResults,
	sparkResultsGenerated,
	TUTORIAL_EVENT_TYPES,
	tutorialEmitter,
} from '../tutorials/tutorialEvents';
import { TopicAnalysis } from '../../../../src/commonTypes';
import { PHRASE_TOOL_UI } from '../../../../src/commonConstants';
import * as ncApi from '../apiRequests';
import { clearFlowyHighlight, highlightFlowyTarget } from './flowyHighlight';
import { toggleToolContainer } from './toolsUI';
import { InspireGraph } from '../graphs';
import { resolveSearchSourceNode } from '../../../../src/services/board/searchSourceNode';
import type { NCNode } from '../../../../src/commonTypes';

type SparkDisplayContext = {
	boardId: string;
	seedLabel: string;
	contextPath: string[];
	rawSuggestions: TopicAnalysis[];
	isFromTopic: boolean;
	addSingleNode: (word: TopicAnalysis) => void;
	enrichedPool: TopicAnalysis[];
	sliderValue: number;
};

let activeSparkDisplay: SparkDisplayContext | null = null;
let sparkEnrichRequestId = 0;
let rememberedSparkSource: NCNode | null = null;

function liveSparkSource(node: NCNode | null | undefined): NCNode | null {
	if (!node?.uid) return null;
	return globalThis.neuroCreate.graph?.getNode(node.uid) || null;
}

export function rememberSparkSourceNode(node: NCNode | null | undefined): NCNode | null {
	const live = liveSparkSource(node);
	if (live) rememberedSparkSource = live;
	return live;
}

const phraseMatchKey = (phrase: TopicAnalysis) => phrase.rawKey || phrase.key;

const removePhraseFromPool = (pool: TopicAnalysis[], phrase: TopicAnalysis): TopicAnalysis[] => {
	const key = phraseMatchKey(phrase);
	const label = phrase.key.trim().toLowerCase();
	return pool.filter(
		(item) => phraseMatchKey(item) !== key && item.key.trim().toLowerCase() !== label
	);
};

const getVisibleSparkPhrases = (pool: TopicAnalysis[]): TopicAnalysis[] =>
	pool.slice(0, PHRASE_TOOL_UI.SPARK_VISIBLE_COUNT);

export const resetSpark = () => {
	const sparkContainer = document.querySelector('#spark-container')!;
	const notFromTopic = document.querySelector('#not-from-topic')!;
	notFromTopic.innerHTML = '';
	const loadingIndicator = sparkContainer.querySelector('.loading')!;
	const pairsList = sparkContainer.querySelector('.pairs-list')!;

	removeChildren(pairsList);
	clearFlowyHighlight();
	loadingIndicator.classList.remove('hidden');
	activeSparkDisplay = null;
};

type BackendPhraseResult = {
	id?: string;
	title?: string;
	label?: string;
	snippet?: string;
	topics?: string[];
};

const renderSparkLoading = () => {
	const pairsList = document.querySelector('#spark-container .pairs-list')!;
	pairsList.innerHTML = '<p class="phrase-loading"><em>Preparing phrases…</em></p>';
};

const renderSparkPhrases = (
	phrases: TopicAnalysis[],
	isFromTopic: boolean,
	emitResultsEvent: boolean
) => {
	const sparkContainer = document.querySelector('#spark-container')!;
	const notFromTopic = document.querySelector('#not-from-topic')!;
	const pairsList = sparkContainer.querySelector('.pairs-list')!;

	removeChildren(pairsList);

	if (!phrases.length) {
		pairsList.innerHTML = '<p><em>No results</em></p>';
	}

	if (!isFromTopic) {
		notFromTopic.innerHTML = 'There is no result from current selected PDF.';
	} else {
		notFromTopic.innerHTML = '';
	}

	for (const [index, phrase] of phrases.entries()) {
		const itemEl = document.createElement('li');
		itemEl.className = 'phrase-item';
		itemEl.dataset.resultId = phraseMatchKey(phrase);
		itemEl.dataset.resultIndex = String(index);
		itemEl.dataset.resultTitle = phrase.key;
		itemEl.dataset.resultSnippet = phrase.key;
		const labelEl = document.createElement('span');
		labelEl.className = 'phrase-item__label';
		labelEl.textContent = phrase.key;
		itemEl.append(labelEl);
		itemEl.onclick = () => {
			const context = activeSparkDisplay;
			if (!context) {
				return;
			}
			itemEl.remove();
			clickedSparkResults(phrase.key);
			context.addSingleNode(phrase);
			context.enrichedPool = removePhraseFromPool(context.enrichedPool, phrase);
			renderSparkPhrases(
				getVisibleSparkPhrases(context.enrichedPool),
				context.isFromTopic,
				false
			);
		};
		pairsList.append(itemEl);
	}

	if (emitResultsEvent) {
		sparkResultsGenerated(phrases.map((phrase) => phrase.key));
	}
	resizeLongSuggestionsToFit();
};

export const highlightSparkResult = ({
	resultId,
	resultIds,
	resultIndex,
	label,
}: {
	resultId?: string | null;
	resultIds?: string[] | null;
	resultIndex?: number | null;
	label?: string | null;
}) => {
	const container = document.querySelector('#spark-container');
	if (!container) return false;
	const normalizedLabel = (label || '').trim().toLowerCase();
	const resultIdSet = new Set([...(resultId ? [resultId] : []), ...(resultIds || [])]);
	let matched = false;
	container.querySelectorAll<HTMLLIElement>('.pairs-list li').forEach((item) => {
		const title = (item.dataset.resultTitle || '').trim().toLowerCase();
		const snippet = (item.dataset.resultSnippet || '').trim().toLowerCase();
		const idMatches = Boolean(item.dataset.resultId && resultIdSet.has(item.dataset.resultId));
		const indexMatches = Boolean(
			typeof resultIndex === 'number' && item.dataset.resultIndex === String(resultIndex)
		);
		const labelMatches = Boolean(
			normalizedLabel &&
				(title === normalizedLabel ||
					snippet === normalizedLabel ||
					title.includes(normalizedLabel) ||
					snippet.includes(normalizedLabel) ||
					normalizedLabel.includes(title) ||
					normalizedLabel.includes(snippet))
		);
		const isMatch = indexMatches || idMatches || labelMatches;
		item.classList.toggle('flowy-recommended-result', isMatch);
		if (isMatch && !matched) {
			matched = true;
			highlightFlowyTarget({
				target: item,
				label: 'Flowy pick',
			});
		}
	});
	return matched;
};

const resolveBackendSparkNode = ({
	selectedNodeIds,
	selectedNodeLabels,
	candidate,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	candidate?: NCNode | null;
}): NCNode | null => {
	const graph = globalThis.neuroCreate.graph;
	return rememberSparkSourceNode(
		resolveSearchSourceNode({
			nodes: graph?.nodes || [],
			selectedNodeIds,
			selectedNodeLabels,
			fallback: liveSparkSource(candidate) || liveSparkSource(rememberedSparkSource) || null,
		})
	);
};

export const openBackendSparkPanel = ({
	selectedNodeIds,
	selectedNodeLabels,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
}) => {
	const node = resolveBackendSparkNode({ selectedNodeIds, selectedNodeLabels });
	rememberSparkSourceNode(node);
	if (node) {
		toggleToolContainer('spark', node);
	}
	document.querySelector('#spark-container')?.classList.remove('hidden');
	resetSpark();
};

export const showBackendSparkResults = ({
	results,
	selectedNodeIds,
	selectedNodeLabels,
	recommendedResultId,
	recommendedResultIds,
}: {
	results: BackendPhraseResult[];
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	recommendedResultId?: string | null;
	recommendedResultIds?: string[];
}) => {
	const container = document.querySelector('#spark-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const pairsList = container.querySelector('.pairs-list')!;
	const seedNode = rememberSparkSourceNode(
		resolveBackendSparkNode({ selectedNodeIds, selectedNodeLabels })
	);
	loadingIndicator.classList.add('hidden');
	removeChildren(pairsList);
	clearFlowyHighlight();
	const recommendedIds = new Set([
		...(recommendedResultId ? [recommendedResultId] : []),
		...(recommendedResultIds || []),
	]);

	results.forEach((entry, index) => {
		const label = entry.label || entry.title || entry.snippet || '';
		if (!label) return;
		const resultId = entry.id || label || `spark-${index}`;
		const itemEl = document.createElement('li');
		itemEl.className = 'phrase-item';
		itemEl.dataset.resultId = resultId;
		itemEl.dataset.resultIndex = String(index);
		itemEl.dataset.resultTitle = label;
		itemEl.dataset.resultSnippet = entry.snippet || label;
		if (recommendedIds.has(resultId)) {
			itemEl.classList.add('flowy-recommended-result');
		}
		const labelEl = document.createElement('span');
		labelEl.className = 'phrase-item__label';
		labelEl.textContent = label;
		itemEl.append(labelEl);
		itemEl.onclick = () => {
			const graph = globalThis.neuroCreate.graph as InspireGraph | undefined;
			const linkTarget =
				resolveBackendSparkNode({ selectedNodeIds, selectedNodeLabels }) ||
				liveSparkSource(seedNode) ||
				liveSparkSource(rememberedSparkSource);
			if (graph && linkTarget) {
				graph.addSingleNode(linkTarget, label, undefined, entry.topics || []);
				clickedSparkResults(label);
				itemEl.remove();
				clearFlowyHighlight();
			}
		};
		pairsList.append(itemEl);
	});

	const resultIdsToHighlight = [...recommendedIds];
	if (resultIdsToHighlight.length) {
		window.setTimeout(() => {
			highlightSparkResult({ resultIds: resultIdsToHighlight });
		}, 80);
	}
	resizeLongSuggestionsToFit();
};

const refreshSparkDisplay = async (sliderValue: number) => {
	const context = activeSparkDisplay;
	if (!context) {
		return;
	}

	context.sliderValue = sliderValue;
	const requestId = ++sparkEnrichRequestId;
	renderSparkLoading();

	try {
		const enriched = await ncApi.enrichSparkPhrases(context.boardId, {
			seedLabel: context.seedLabel,
			contextPath: context.contextPath,
			sliderValue,
			items: context.rawSuggestions,
		});
		if (requestId !== sparkEnrichRequestId) {
			return;
		}
		context.enrichedPool = enriched;
		renderSparkPhrases(
			getVisibleSparkPhrases(enriched),
			context.isFromTopic,
			true
		);
	} catch {
		if (requestId !== sparkEnrichRequestId) {
			return;
		}
		const fallbackCount = Math.min(
			PHRASE_TOOL_UI.SPARK_VISIBLE_COUNT + PHRASE_TOOL_UI.SPARK_BUFFER_COUNT,
			context.rawSuggestions.length
		);
		const index = Math.max(
			0,
			Math.floor((context.rawSuggestions.length - fallbackCount) * (sliderValue / 100))
		);
		context.enrichedPool = context.rawSuggestions.slice(index, index + fallbackCount);
		renderSparkPhrases(
			getVisibleSparkPhrases(context.enrichedPool),
			context.isFromTopic,
			true
		);
	}
};

export const setupSparkResults = async (
	boardId: string,
	seedLabel: string,
	suggestions: Array<TopicAnalysis>,
	isFromTopic: boolean,
	addSingleNode: (word: TopicAnalysis) => void,
	contextPath: string[] = []
) => {
	const container = document.querySelector('#spark-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const lateralThinkingSlider = container.querySelector<HTMLInputElement>("input[type='range']")!;

	loadingIndicator.classList.add('hidden');

	const sliderValue = parseInt(lateralThinkingSlider.value, 10);
	activeSparkDisplay = {
		boardId,
		seedLabel,
		contextPath,
		rawSuggestions: suggestions,
		isFromTopic,
		addSingleNode,
		enrichedPool: [],
		sliderValue,
	};

	await refreshSparkDisplay(sliderValue);

	lateralThinkingSlider.oninput = () => {
		tutorialEmitter.emit(
			TUTORIAL_EVENT_TYPES.lateralSliderMoved,
			parseInt(lateralThinkingSlider.value, 10)
		);
	};

	lateralThinkingSlider.onchange = () => {
		void refreshSparkDisplay(parseInt(lateralThinkingSlider.value, 10));
	};
};
